import { useNavigate } from "@tanstack/react-router"
import type { ReactNode } from "react"

import type { NavSectionId } from "../../shell/nav-items"
import { Button } from "../../ui/button"
import { placeholderText } from "./placeholder-text"
import styles from "./placeholder-screen.module.css"

/**
 * Заглушка раздела («Заглушка» в макете «Мастер, доступ и заглушка — П1»).
 *
 * Пункт в навигации стоит заранее и не переезжает: меню, которое перестраивается
 * от обновления к обновлению, человек перестаёт читать глазами и начинает искать
 * каждый раз заново.
 *
 * ЭКРАН ОТВЕЧАЕТ НА ДВА ВОПРОСА: что здесь будет и где смотреть это сейчас. Общее
 * «раздел в разработке» не отвечает ни на один, и владелец решает, что программа
 * недоделана целиком. Кнопка ведёт туда, где это уже работает, — и появляется только
 * когда такое место есть.
 */
export function PlaceholderScreen(props: {
  readonly id?: NavSectionId
  readonly label: string
  /** Оставлено для экранов мастера, которые зовут заглушку со своим текстом. */
  readonly summary?: string
}): ReactNode {
  const navigate = useNavigate()
  const known = props.id === undefined ? null : placeholderText(props.id, props.label)
  /*
   * Свой текст раздела важнее общего. У пяти разделов он написан прямо в маршруте
   * («Здесь появятся филиалы, их часовые пояса, часы работы и правила записи») —
   * и, пока заглушка его выбрасывала, владелец читал общую фразу и не узнавал,
   * что правила записи уже работают этажом ниже.
   */
  const text =
    known === null || (known.action === null && props.summary !== undefined)
      ? {
          head: known?.head ?? `${props.label} появятся в следующем обновлении`,
          text: props.summary ?? known?.text ?? "Остальные разделы работают.",
          action: known?.action ?? null,
        }
      : known

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <h2 className={styles.title}>{text.head}</h2>
        <p className={styles.text}>{text.text}</p>
        {text.action === null ? null : (
          <div className={styles.actions}>
            <Button
              variant="secondary"
              onClick={() => {
                void navigate({ to: text.action?.to ?? "/journal" })
              }}
            >
              {text.action.label}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
